import { useEffect, useMemo, useState } from "react";
import TabSelector from "../../../../common/components/tab-selector/TabSelector";
import { AssessmentStatus, Leads, PaymentPlan, StoreProductDetail } from "../../../../model/leads/leads";
import styles from "../RequestPage.module.scss";
import { RequestType } from "./RequestTabs";
import LeadsProductCard from "../../../../common/components/leads-product-card/LeadsProductCard";
import RecommendProductDialogue from "./recommend-product-dialogue/RecommendProductDialogue";
import { formatCurrency } from "../../../../utils";
import {
	useChoosePhysicalLoadAssessmentMutation,
	useRecommendNoProductsMutation,
	useRecommendProductsMutation,
	useSendCustomerProductsMutation,
	useGetRequestByIdQuery,
	useGetSystemRecommendedProductsQuery,
	useSchedulePhysicalLoadAssessmentMutation,
	useReSchedulePhysicalLoadAssessmentMutation,
} from "../../../../service/leads-service";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils/error-handler";
import ConfirmationDialog from "../../../../common/components/dialog/ConfirmationDialog";
import { CalendarClock, ClockLoader, PenIcon } from "../../../../assets/icons";
import { useRecommendProducts } from "./recommend-product-dialogue/useRecommendedProducts";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { Money } from "../../../../model/common/money";
import { AssessmentStatusBadge } from "./AssessmentStatusBadge";
import { ScheduleAssessmentModal } from "./ScheduleAssessmentModal";
import { ScheduleAction, ScheduleLoadAssessmentPayload, SelectedAppliance } from "../request.type";
import { convertToApiFormat, getDefaultValues } from "../request.util";
import { ChangeScheduleStatusModal } from "./ChangeScheduleStatusModal";
import ApplianceSelector from "./appliance-selector/ApplianceSelector";

type ActionType = "reject" | "accept" | "physical" | null;

interface RequestDetailProps {
	data: Leads;
	type: RequestType;
	onBack: () => void;
}

const RequestDetail = ({ data, type, onBack }: RequestDetailProps) => {
	const [selectedTab, setSelectedTab] = useState(1);
	const [showDialogue, setShowDialogue] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
	const [showScheduleModalDialogue, setShowScheduleModalDialogue] = useState(false);
	const [currentAction, setCurrentAction] = useState<ActionType>(null);
	const [currentScheduleAction, setCurrentScheduleAction] = useState<ScheduleAction>("schedule");
	const [showApplianceSelector, setShowApplianceSelector ] = useState(false);

	const [recommendNoProducts, { isLoading: rejectingRequest }] = useRecommendNoProductsMutation();
	const [recommendProducts, { isLoading: isRecommendingProducts }] = useRecommendProductsMutation();
	const [choosePhysicalAssessment, { isLoading: choosingPhysicalAssessment }] = useChoosePhysicalLoadAssessmentMutation();
	const [sendCustomerProducts, { isLoading: isSendingProducts }] = useSendCustomerProductsMutation();
	const [selectedRecommendedProducts, setSelectedRecommendedProducts] = useState<StoreProductDetail[]>([]);

	const { data: item = {} as Leads, isLoading } = useGetRequestByIdQuery(data.id);
	const { data: recommendedProducts = [], isLoading: isSystemRecommendedLoading } = useGetSystemRecommendedProductsQuery(data.id);
	const [schedulePhysicalAssessment, { isLoading: isScheduling }] = useSchedulePhysicalLoadAssessmentMutation();
	const [reschedulePhysicalAssessment, { isLoading: isRescheduling }] = useReSchedulePhysicalLoadAssessmentMutation();

	const ScheduleDate  = getDefaultValues(item?.assessmentDetails?.physicalLoadAssessment?.scheduledDate);

	const combinedAppliances = useMemo(() => {
		const digitalAppliances = item?.assessmentDetails?.digitalLoadAssessment?.appliances || [];
		const physicalAppliances = item?.assessmentDetails?.physicalLoadAssessment?.appliances || [];

		const applianceMap = new Map();

		digitalAppliances.forEach(appliance => {
			applianceMap.set(appliance.name, {
				name: appliance.name,
				digital: {
					quantity: appliance.quantity,
					usage: appliance.usage,
				},
				physical: null,
			});
		});

		physicalAppliances.forEach(appliance => {
			if (applianceMap.has(appliance.name)) {
				applianceMap.get(appliance.name).physical = {
					quantity: appliance.quantity,
					usage: appliance.usage,
				};
			} else {
				applianceMap.set(appliance.name, {
					name: appliance.name,
					digital: null,
					physical: {
						quantity: appliance.quantity,
						usage: appliance.usage,
					},
				});
			}
		});

		return Array.from(applianceMap.values());
	}, [item?.assessmentDetails?.digitalLoadAssessment, item?.assessmentDetails?.physicalLoadAssessment]);

	const { selectedProducts, handleProductsSelected } = useRecommendProducts({
		onSave: (products: StoreProductDetail[]) => {
			setSelectedRecommendedProducts([...products]);
			if (products.length > 0 && item?.id) {
				const productIds = products.map(product => product.id || "").filter(id => id !== "");

				sendCustomerProducts({
					recommendationId: item?.id,
					products: productIds,
				})
					.unwrap()
					.then(() => {
						toast.success("Products sent to customer successfully");
						setShowDialogue(false);
						// Optionally navigate back or refresh the data
						// onBack();
					})
					.catch(err => {
						toast.error(getErrorMessage(err?.data));
					});
			}
		},
	});

	const toggleApplianceSelector = () => {
		setShowApplianceSelector(!showApplianceSelector);
	}

	const handleOpenScheduleModal = (action: ScheduleAction) => {
		setCurrentScheduleAction(action);
		setShowScheduleModalDialogue(true);
	};

	const handleScheduleSubmit = (formData: { date: Date; time: { hour: string; minute: string; period: "AM" | "PM" } }) => {
		const payload: ScheduleLoadAssessmentPayload = {
			recommendationId: item.id,
			date: convertToApiFormat(formData.date, formData.time),
			isConfirmed: true,
		};

		const mutation = currentScheduleAction === "schedule" ? schedulePhysicalAssessment : reschedulePhysicalAssessment;
		const successMessage = currentScheduleAction === "schedule" ? "Physical assessment scheduled successfully" : "Physical assessment rescheduled successfully";

		mutation(payload)
			.unwrap()
			.then(() => {
				toast.success(successMessage);
				setShowScheduleModalDialogue(false);
			})
			.catch(err => {
				toast.error(getErrorMessage(err?.data));
			});
	};

	const handleStatusChangeSubmit = (selectedStatus: "progress" | "completed") => {
		if (selectedStatus === "progress") {
			handleChoosePhysicalLoadAssessment();
		}

		if (selectedStatus === "completed") {
			toggleApplianceSelector();
		}
	};

	const changeTab = (tabId: number) => {
		setSelectedTab(tabId);
	};

	const handleShowConfirmation = (action: ActionType) => {
		setCurrentAction(action);
		setShowConfirmation(true);
	};

	const handleShowStatusChangeDialog = () => {
		setShowStatusChangeDialog(true);
	};

	const tab = () => [
		{ id: 1, title: "STEP 1  Accept Load Assessment", onClick: () => changeTab(1) },
		{ id: 2, title: "STEP 2  Recommend Product", onClick: () => changeTab(2), disabled: type === RequestType.New },
	];

	// To call the mutation
	const handleNoProducts = () => {
		recommendNoProducts({
			recommendationId: item.id,
		})
			.unwrap()
			.then(res => {
				toast.success("Request rejected");
				setShowConfirmation(false);
				setCurrentAction(null);
				onBack();
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const handleRecommendProducts = () => {
		if (recommendedProducts.length === 0) {
			toast.warning("No system recommended products");
			return;
		}
		const productIds = recommendedProducts.map(product => product.id);

		//.map(product => product.id);
		recommendProducts({
			recommendationId: item.id,
			productIds: productIds,
		})
			.unwrap()
			.then(() => {
				toast.success("Product recommended successfully");
				onBack();
				setShowConfirmation(false);
				setCurrentAction(null);
			})
			.catch(err => {
				toast.error(getErrorMessage(err?.data));
			});
	};

	const handleChoosePhysicalLoadAssessment = () => {
		choosePhysicalAssessment({
			recommendationId: item.id,
		})
			.unwrap()
			.then(() => {
				toast.success("Physical Load Assessment opted for");
				setShowConfirmation(false);
				setCurrentAction(null);
				onBack();
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const handleConfirmAction = () => {
		switch (currentAction) {
			case "reject":
				handleNoProducts();
				break;
			case "accept":
				handleRecommendProducts();
				break;
			case "physical":
				handleChoosePhysicalLoadAssessment();
				break;
			default:
				break;
		}
	};

	const getConfirmationContent = () => {
		switch (currentAction) {
			case "reject":
				return {
					title: "Reject Request",
					description: "Are you sure you want to reject this customer's request? This action cannot be undone.",
					btnText: "Reject Request",
					actionLoading: rejectingRequest,
				};
			case "accept":
				return {
					title: "Accept Ekedp PowerApp Recommendation",
					description: "Are you sure you want to accept the Ekedp PowerApp recommendation for this customer?",
					btnText: "Accept Recommendation",
					actionLoading: isRecommendingProducts,
				};
			case "physical":
				return {
					title: "Schedule Physical Load Assessment",
					description: "Are you sure you want to schedule a physical load assessment for this customer?",
					btnText: "Schedule Assessment",
					actionLoading: choosingPhysicalAssessment,
				};
			default:
				return {
					title: "Confirmation",
					description: "Are you sure you want to proceed?",
					btnText: "Proceed",
					actionLoading: false,
				};
		}
	};

	const renderLoadAssessmentTab = () => {
		const isLoading = rejectingRequest || isRecommendingProducts;
		return (
			<>
				{/* Digital Load Assessment Section */}
				<section className={styles.details_section}>
					<div className={styles.details_row}>
						<h2 className={`${styles.details_section_title} ${styles.no_underline}`}>LOAD ASSESSMENT DETAILS</h2>
						<h2 className={`${styles.details_section_title} ${styles.no_underline}`}>DIGITAL</h2>
						{item?.assessmentDetails?.physicalLoadAssessment && <h2 className={`${styles.details_section_title} ${styles.no_underline}`}>PHYSICAL</h2>}
					</div>

					<div className={styles.details_grid}>
						<div className={styles.details_row}>
							<strong>Summary</strong>
						</div>
						<div className={styles.details_row}>
							<span className={styles.details_label}>Total Appliances added</span>
							<span className={styles.details_value}>{item?.assessmentDetails?.digitalLoadAssessment.appliances.length} Appliances</span>
							{item?.assessmentDetails?.physicalLoadAssessment && (
								<span className={styles.details_value}>{item?.assessmentDetails?.physicalLoadAssessment?.appliances?.length} Appliances</span>
							)}
						</div>
						<div className={styles.details_row}>
							<span className={styles.details_label}>kVA Needed</span>
							<span className={styles.details_value}>{item?.assessmentDetails?.digitalLoadAssessment.kva}</span>
							<span className={styles.details_value}>{item?.assessmentDetails?.physicalLoadAssessment?.kva}</span>
						</div>
						<div className={styles.details_row}>
							<span className={styles.details_label}>Total watts added</span>
							<span className={styles.details_value}>{item?.assessmentDetails?.digitalLoadAssessment.totalWatts}</span>
							<span className={styles.details_value}>{item?.assessmentDetails?.physicalLoadAssessment?.totalWatts}</span>
						</div>
						<div className={styles.details_row}>
							<span className={styles.details_label}>Customer's Budget</span>
							<span className={styles.details_value}>
								{item.assessmentDetails && item.assessmentDetails.paymentPlan != PaymentPlan.PayAsYouGo
									? new Money(
											item?.assessmentDetails?.paymentPlanDetails?.alternatePowerExpense.currency,
											item?.assessmentDetails?.paymentPlanDetails?.alternatePowerExpense.amount
									  ).toLocaleString()
									: null}
							</span>
						</div>
					</div>
				</section>

				{(item?.assessmentDetails?.digitalLoadAssessment.appliances.length > 0 || item?.assessmentDetails?.physicalLoadAssessment?.appliances?.length > 0) && (
					<section className={styles.details_section}>
						<div className={styles.details_row}>
							<h2 className={`${styles.details_section_title} ${styles.no_underline}`}>APPLIANCES</h2>
							<h2 className={`${styles.details_section_title} ${styles.no_underline}`}>DIGITAL</h2>
							{item?.assessmentDetails?.physicalLoadAssessment && <h2 className={`${styles.details_section_title} ${styles.no_underline}`}>PHYSICAL</h2>}
						</div>

						<div className={styles.details_grid}>
							{combinedAppliances.map((appliance, index) => (
								<div key={`${appliance.name}-${index}`} className={styles.details_row}>
									<span className={styles.details_label}>{appliance.name}</span>

									{/* Digital column */}
									<span className={`${styles.details_row} ${styles.appliance_values}`}>
										{appliance.digital ? (
											<>
												Qty: {appliance.digital.quantity}
												<span className={styles.appliance_stroke}>|</span> Daily Usage: {appliance.digital.usage}hr(s)
											</>
										) : (
											<span style={{ color: "#ccc" }}>-</span>
										)}
									</span>

									{/* Physical column - only show if physical assessment exists */}
									{item?.assessmentDetails?.physicalLoadAssessment && (
										<span className={`${styles.details_row} ${styles.appliance_values}`}>
											{appliance.physical ? (
												<>
													Qty: {appliance.physical.quantity}
													<span className={styles.appliance_stroke}>|</span> Daily Usage: {appliance.physical.usage}hr(s)
												</>
											) : (
												<span style={{ color: "#ccc" }}>-</span>
											)}
										</span>
									)}
								</div>
							))}
						</div>
					</section>
				)}

				{(item.status === AssessmentStatus.CarringOutPhysicalLoadAssessment ||
					item.status === AssessmentStatus.RescheduledPhysicalLoadAssessment ||
					item.status === AssessmentStatus.ScheduledPhysicalLoadAssessment ||
					item.status === AssessmentStatus.OverduePhysicalLoadAssessment) && (
						<>
							<AssessmentStatusBadge status={item.status} onChangeStatus={handleShowStatusChangeDialog} />
							<div className={styles.details_row} style={{ marginTop: 22 }}>
								<span className={styles.details_label}>Schedule Date and Time</span>
								<span className={`${styles.details_value} ${styles.row}`}>
									<span className={`${styles.status_badge} ${styles.status_badge_date}`}> {`${ScheduleDate?.date?.toLocaleString()}`}</span>
									<button className={styles.button} onClick={() => handleOpenScheduleModal("reschedule")} disabled={false} type="button">
										<CalendarClock />
										<span>Reschedule</span>
									</button>
								</span>
							</div>
						</>
					 )}

				<section className={styles.details_section}>
					<h2 className={styles.details_section_title}>RECOMMENDED PRODUCTS</h2>
					<CommonLoadingSpinner loading={isSystemRecommendedLoading}>
						<div className={styles.product_row}>
							{recommendedProducts.length > 0 &&
								recommendedProducts.map((res, index) => {
									const { priceBreakdown, productCapacity, productName, packageDescription, categories, images } = res;
									return (
										<LeadsProductCard
											price={priceBreakdown?.price}
											description={packageDescription}
											capacity={productCapacity}
											productName={productName}
											categoryNames={categories}
											imageSrc={images[0].imageUrl}
											key={`${index}-${res.id}`}
										/>
									);
								})}
						</div>
					</CommonLoadingSpinner>
				</section>

				{/* Footer Actions */}
				{item.status !== AssessmentStatus.SendingProductsThatFit &&
					item.status !== AssessmentStatus.CarringOutPhysicalLoadAssessment &&
					item.status !== AssessmentStatus.RescheduledPhysicalLoadAssessment &&
					item.status !== AssessmentStatus.ScheduledPhysicalLoadAssessment &&
					item.status !== AssessmentStatus.Pending &&
					 item.status !== AssessmentStatus.Final && (
						<div className={styles.detail_actions}>
							<button disabled={isLoading} className={`${styles.btn} ${styles.btn_grey}`} onClick={() => handleShowConfirmation("reject")}>
								{rejectingRequest ? "Rejecting..." : "Reject Request"}
							</button>
							<button disabled={isLoading} className={`${styles.btn} ${styles.btn_primary}`} onClick={() => handleShowConfirmation("accept")}>
								Accept Ekedp PowerApp Recommendation
							</button>
							<button disabled={isLoading} className={`${styles.btn} ${styles.btn_green}`} onClick={() => setShowScheduleModalDialogue(true)}>
								Physical Load Assessment
							</button>
						</div>
					)}
			</>
		);
	};

	const renderRecommendationTab = () => {
		const list = item.assessmentDetails.recommendedProducts.length > 0 ? item.assessmentDetails.recommendedProducts : selectedRecommendedProducts;
		return (
			<>
				{showDialogue && (
					<RecommendProductDialogue
						paymentPlan={item.assessmentDetails.paymentPlan}
						onSaveProducts={handleProductsSelected}
						onClose={() => setShowDialogue(false)}
						loadAssessmentInfo={item?.assessmentDetails?.physicalLoadAssessment ?? item?.assessmentDetails?.digitalLoadAssessment}
						previouslyRecommendedProduct={item?.assessmentDetails?.recommendedProducts ?? []}
					/>
				)}
				{/* Action Section */}
				<section className={styles.details_section}>
					<h2 className={styles.details_section_title}>RECOMMENDED PRODUCTS</h2>
					<div className={styles.product_row}>
						{list.map((res, index) => {
							const { priceBreakdown, productCapacity, productName, packageDescription, categories, images } = res;
							return (
								<LeadsProductCard
									price={priceBreakdown?.price}
									description={packageDescription}
									capacity={productCapacity}
									productName={productName}
									categoryNames={categories}
									imageSrc={images[0].imageUrl}
									key={`${index}-${res.id}`}
								/>
							);
						})}
						{/* <LeadsProductCard subtitle="Up to 50 Hours | 40kVA | 1-12 Years Plan" />*/}
					</div>
				</section>

				{/* Footer Actions */}
				{item.status !== AssessmentStatus.Final && <div className={styles.detail_actions}>
					{item.status !== AssessmentStatus.Pending && (
						<button onClick={() => setShowDialogue(true)} className={`${styles.btn} ${styles.btn_green}`}>
							Recommend Product
						</button>
					)}
					{item.status === AssessmentStatus.Pending && (
						<button disabled={isLoading} className={`${styles.btn} ${styles.btn_green}`} onClick={() => setShowDialogue(true)}>
							Edit Recommendation
						</button>
					)}
				</div>}
			</>
		);
	};

	const confirmationContent = getConfirmationContent();

	return (
		<div className={styles.detail_content}>
			{/* Customer Details Section */}
			{showApplianceSelector ? <ApplianceSelector
			  previouslySelected={item?.assessmentDetails?.physicalLoadAssessment?.appliances}
			  recommendationId={data.id}
			  onSubmit={() => {}}
			  onComplete={() => setShowStatusChangeDialog(false)}
			  onClose={toggleApplianceSelector}
			/> : <CommonLoadingSpinner loading={isLoading}>
				<section className={styles.details_section}>
					<button onClick={onBack}>
						<svg fill="#000000" height="15px" width="15px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 492 492">
							<g>
								<g>
									<path
										d="M464.344,207.418l0.768,0.168H135.888l103.496-103.724c5.068-5.064,7.848-11.924,7.848-19.124
                                    c0-7.2-2.78-14.012-7.848-19.088L223.28,49.538c-5.064-5.064-11.812-7.864-19.008-7.864c-7.2,0-13.952,2.78-19.016,7.844
                                    L7.844,226.914C2.76,231.998-0.02,238.77,0,245.974c-0.02,7.244,2.76,14.02,7.844,19.096l177.412,177.412
                                    c5.064,5.06,11.812,7.844,19.016,7.844c7.196,0,13.944-2.788,19.008-7.844l16.104-16.112c5.068-5.056,7.848-11.808,7.848-19.008
                                    c0-7.196-2.78-13.592-7.848-18.652L134.72,284.406h329.992c14.828,0,27.288-12.78,27.288-27.6v-22.788
                                    C492,219.198,479.172,207.418,464.344,207.418z"
									/>
								</g>
							</g>
						</svg>
					</button>
					<h2 className={styles.details_section_title}>CUSTOMER DETAILS</h2>
					<div className={styles.details_grid}>
						<div className={styles.details_row}>
							<span className={styles.details_label}>Customer Name</span>
							<span className={styles.details_value}>{`${item?.user?.firstName} ${item?.user?.lastName}`}</span>
						</div>
						<div className={styles.details_row}>
							<span className={styles.details_label}>Email</span>
							<span className={styles.details_value}>{item?.user?.email}</span>
						</div>
						<div className={styles.details_row}>
							<span className={styles.details_label}>Phone Number</span>
							<span className={styles.details_value}>{item?.user?.phoneNumber}</span>
						</div>
						{/* <div className={styles.details_row}>
						<span className={styles.details_label}>Level of interest</span>
						<span className={styles.details_value}>
							<span className={`${styles.status_badge} ${styles.status_badge_ready}`}>Ready to Buy</span>
						</span>
					</div> */}
					</div>
				</section>

				<div className={styles.tabContainer}>
					<TabSelector tabs={tab()} selectedTab={selectedTab} theme="small" />
				</div>
				{selectedTab === 1 && renderLoadAssessmentTab()}
				{selectedTab === 2 && renderRecommendationTab()}
				{showConfirmation && (
					<ConfirmationDialog
						actionLoading={confirmationContent.actionLoading}
						btnText={confirmationContent.btnText}
						onCloseDialog={() => setShowConfirmation(false)}
						onConfirm={handleConfirmAction}
						title={confirmationContent.title}
						description={confirmationContent.description}
					/>
				)}
				{showScheduleModalDialogue && (
					<ScheduleAssessmentModal
						onClose={() => setShowScheduleModalDialogue(false)}
						onSubmit={handleScheduleSubmit}
						actionType={currentScheduleAction}
						isLoading={currentScheduleAction === "schedule" ? isScheduling : isRescheduling}
						clientPhone={item.user.phoneNumber}
						scheduledDate={item?.assessmentDetails?.physicalLoadAssessment?.scheduledDate ?? ""}
					/>
				)}

				{showStatusChangeDialog && (
					<ChangeScheduleStatusModal
						onClose={() => setShowStatusChangeDialog(false)}
						onSubmit={handleStatusChangeSubmit}
						currentStatus={AssessmentStatus.ChosingProvider}
						isLoading={choosingPhysicalAssessment}
					/>
				)}
			</CommonLoadingSpinner>}
		</div>
	);
};
export default RequestDetail;
